import { useNavigate, useParams } from 'react-router-dom'
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { IconName } from '../../../../shared/interfaces/icon-picker.model'
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading'
import { LoadingPage } from '../../loading-page'
import AnalyticReportService from '../../../../shared/services/analytic-report/analytic-report.service'
import React, { useContext, useEffect, useState } from 'react'
import { partnerManagedTooltip } from '../../../../shared/utils/partner-managed-utils';
import { IAnalyticReportWithEmissionsAndFilters } from '../../../../shared/services/analytic-report/analytic-report.model'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { Grid, Stack, Typography, useTheme } from '@mui/material'
import { DeleteOutline, DownloadOutlined, SendOutlined } from '@mui/icons-material'
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels'
import { getDisplayLabel as getDisplayLabelUtil } from '../../../../shared/utils/screen-labels'
import { AnalyticsFilterDisplay } from '../../../presentation-components/analytics-components/reports/analytics-filter-display'
import { GraphDonutChart } from '../../../presentation-components/analytics-components/graphs/graph-donut-chart'
import { SeriesBar, buildGenericSeriesStack, valueFormatter } from '../../../../shared/interfaces/analytics'
import { getMixedColorPalette } from '../../../../shared/utils/colors-utils'
import { GraphBarChartStacked } from '../../../presentation-components/analytics-components/graphs/graph-bar-chart-stacked'
import { GraphBarChart } from '../../../presentation-components/analytics-components/graphs/graph-bar-chart'
import { EmissionsTable } from '../../../presentation-components/emissons-components/overview/emissions-table'
import { DownloadStatusContext } from '../../../../context/download-status/download-status'
import FileDownloadService from '../../../../shared/services/file-download/file-download.service'
import { SendAnalyticsReportDialog } from '../../../dialogs/analytics/send-analytics-report-dialog'

export const AnalyticsReportsDisplayPage = () => {
  const theme = useTheme();
  const colors = getMixedColorPalette(theme.palette.primary.main);
  const { id } = useParams()
  const navigate = useNavigate()
  //Services
  const analyticReportService = new AnalyticReportService()
  const fileDownloadService = new FileDownloadService()
  //Context
  const screenLabelsContext = useContext(ScreenLabelsContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  const downloadStatusContext = useContext(DownloadStatusContext)
  //State
  const [report, setReport] = useState<IAnalyticReportWithEmissionsAndFilters>()
  //Table State
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [sendDialogOpen, setSendDialogOpen] = useState<boolean>(false)

  useEffect(() => {
    if (id) {
      analyticReportService.getOneWithFiltersAndEmissionsPagination(id, inputPage, perPage, orderBy, order, true)
        .then(response => {
          setReport(response)
          setIsLoading(false)
          setTotalRecords(response.emissions.count);
        })
        .catch(err => {
          setIsLoading(false)
          console.log(err)
        }
        )
    }
  }, [id, inputPage, perPage, orderBy, order, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [order, orderBy])

  const getDisplayLabel = (category?: string): string => getDisplayLabelUtil(category, screenLabelsContext.getLabel)

  const deleteHandler = () => {
    userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to delete this analytic report? This action cannot be undone.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
      .then(response => {
        if (response && id) {
          if (!isFormSubmitting) {
            analyticReportService.remove(id)
              .then(response => {
                setIsFormSubmitting(false)
                navigate(`/analytics/reports`)
                userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
              })
              .catch(err => {
                console.log(err)
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                setIsFormSubmitting(false)
              })
          }
        }
      })
      .catch(err => {
        console.log(err)
      })
  }

  const downloadCsvHandler = () => {
    if (id) {
      fileDownloadService.createFileDownloadEntry('analyticReportCsv', null, id)
        .then(response => {
          downloadStatusContext.startDownloadTracking(response.id)
        })
        .catch(err => {
          console.log(err)
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
        })
    }
  }


  return (
    <PageOuterContainer>
      {report &&
        <PageHeading
          includeBackButton={true}
          backButtonLink='/analytics/reports'
          backButtonText='Back to analytic reports'
          title={`${report?.name} Analytics Report`}
          includeSecondaryActionButton={true}
          secondaryActionButtonIcon={IconName.EditOutlined}
          secondaryActionButtonHandler={() => navigate(`/analytics/reports/edit-report/${id}`)}
          secondaryActionButtonText='Edit Report'
          secondaryActionButtonVariant='contained'
          // A report pushed from a partner's Library is theirs to maintain. Edit and Delete
          // are greyed rather than removed — a control that vanishes reads as a bug, not a
          // permission — and the chip says who manages it. Download and Send stay enabled:
          // they are reads, and the backend does not block them.
          disableSecondaryActionButton={report.canClientEdit === false}
          showChip={Boolean(report.isPartnerManaged)}
          chipColor='default'
          chipLabel={partnerManagedTooltip(report)}
          menuActions={[
            { label: 'Download CSV', icon: <DownloadOutlined />, onClick: downloadCsvHandler },
            { label: 'Send by email', icon: <SendOutlined />, onClick: () => setSendDialogOpen(true) },
            { label: 'Delete Report', icon: <DeleteOutline />, onClick: deleteHandler, disabled: report.canClientEdit === false },
          ]}
          hasTabs={false}
        />}
      <LoadingPage pageReady={!isLoading}>
        {report &&
          <AnalyticsFilterDisplay
            usingCustomDates={report.usingCustomDates}
            filters={report.filters}
          />}
        <Stack sx={{ mb: 2 }} />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {report && report.pieChartCategory &&
            <Grid item xl={!report.stackedChartCategory && !report.barChartCategory ? 12 : 4} lg={!report.stackedChartCategory && !report.barChartCategory ? 12 : 4} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
              <GraphDonutChart
                title={`Emission Breakdown by ${getDisplayLabel(report.pieChartCategory)}`}
                dataset={report.charts.pie}
                colors={colors} />
            </Grid>}

          {report && report.barChartCategory &&
            <Grid item xl={!report.pieChartCategory ? 12 : 8} lg={!report.pieChartCategory ? 12 : 8} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
              <GraphBarChart
                title={`Emission Breakdown by ${getDisplayLabel(report.barChartCategory)}`}
                dataset={report.charts.bar}
                series={SeriesBar}
                dataKey='label'
                layout='horizontal'
                colors={colors}
              />
            </Grid>}

          {report && report.stackedChartCategory &&
            <Grid item xl={report.pieChartCategory && !report.barChartCategory ? 8 : 12} lg={report.pieChartCategory && !report.barChartCategory ? 8 : 12} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
              <GraphBarChartStacked
                title={`Emission History for ${getDisplayLabel(report.stackedChartCategory)}`}
                dataset={report.charts.stacked}
                series={buildGenericSeriesStack(report.charts.stacked, valueFormatter)}
                colors={colors}
              />
            </Grid>}
        </Grid>
        <Typography variant='subtitle1' sx={{ flex: 1, color: 'text.primary', mb: 1 }}><b>Table of Emissions</b></Typography>
        {report && report.emissions?.rows && (
          <EmissionsTable
            emissions={report.emissions.rows}
            inputPage={inputPage}
            setInputPage={setInputPage}
            totalRecords={totalRecords}
            perPage={perPage}
            setPerPage={setPerPage}
            setOrderBy={setOrderBy}
            setOrder={setOrder}
            viewPath="/emissions/overview"
            backLink={`/analytics/reports/${id}`}
            backText="Back to report"
          />
        )}
      </LoadingPage>

      <SendAnalyticsReportDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        reportId={id ?? ''}
        reportName={report?.name ?? ''}
      />
    </PageOuterContainer>
  )
}